/**
 * TabSyncService — Cross-tab synchronization for WebRTC/Softphone call state.
 *
 * Uses the BroadcastChannel API to coordinate call handling across browser tabs.
 *
 * KEY BEHAVIORS:
 * 1. LEADER ELECTION: Only one tab (the "leader") shows the ringtone and incoming
 *    call toast. The leader is the tab that has focus, or if no tab has focus, the
 *    first tab that received the INVITE.
 * 2. CALL ANSWERED: When a call is answered/rejected on any tab, ALL other tabs
 *    immediately dismiss their ringing UI and reject their local SIP session.
 * 3. CALL STATE SYNC: Active call state (connected, on-hold, etc.) is broadcast so
 *    other tabs can show a passive "call in progress" indicator.
 *
 * Each tab independently receives INVITEs from the PBX (since they all register the
 * same extension). This service coordinates which tab "owns" the call interaction.
 */

export interface TabCallState {
  tabId: string;
  isRegistered: boolean;
  incomingCall: {
    remoteIdentity: string;
    displayName: string;
  } | null;
  /** Active/connected call details (null if no active call) */
  activeCall: {
    remoteIdentity: string;
    displayName: string;
    startTime: number;
    isOnHold: boolean;
  } | null;
  callProgress: string;
  hasActiveCall: boolean;
  sessionCount: number;
  lastUpdated: number;
  /** Whether this tab currently has browser focus */
  hasFocus: boolean;
}

type TabSyncEventType =
  | "callStateChanged"
  | "callAnswered"
  | "callRejected"
  | "callCommand"
  | "tabClosed";

interface TabSyncMessage {
  type: TabSyncEventType;
  tabId: string;
  payload: any;
}

/** Commands that can be sent from a controlling tab to the session-owning tab */
export type CallCommand = "mute" | "unmute" | "hold" | "unhold" | "hangup" | "sendDTMF";

export interface CallCommandPayload {
  command: CallCommand;
  /** Optional data (e.g., DTMF digit) */
  data?: string;
}

type CallAnsweredListener = () => void;
type StateListener = (state: TabCallState) => void;
type CommandListener = (cmd: CallCommandPayload) => void;

const CHANNEL_NAME = "softphone-tab-sync";
const TAB_ID = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const STALE_THRESHOLD_MS = 10000;

class TabSyncService {
  private channel: BroadcastChannel | null = null;
  private stateListeners: Array<StateListener> = [];
  private callAnsweredListeners: Array<CallAnsweredListener> = [];
  private commandListeners: Array<CommandListener> = [];
  private remoteTabs: Map<string, TabCallState> = new Map();
  private localState: TabCallState;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.localState = {
      tabId: TAB_ID,
      isRegistered: false,
      incomingCall: null,
      activeCall: null,
      callProgress: "idle",
      hasActiveCall: false,
      sessionCount: 0,
      lastUpdated: Date.now(),
      hasFocus: document.hasFocus(),
    };
  }

  /**
   * Initialize the BroadcastChannel and start listening.
   */
  init(): void {
    if (this.channel) return;

    try {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.onmessage = (event: MessageEvent<TabSyncMessage>) => {
        this.handleMessage(event.data);
      };

      // Track focus state
      window.addEventListener("focus", this.handleFocus);
      window.addEventListener("blur", this.handleBlur);

      // Heartbeat for presence detection
      this.heartbeatTimer = setInterval(() => {
        this.localState.lastUpdated = Date.now();
        this.localState.hasFocus = document.hasFocus();
        this.broadcast("callStateChanged", this.localState);
        this.pruneStale();
      }, 3000);

      // Broadcast initial state
      this.broadcast("callStateChanged", this.localState);

      // Clean up on tab close
      window.addEventListener("beforeunload", this.handleBeforeUnload);
    } catch {
      console.warn("[TabSyncService] BroadcastChannel not supported");
    }
  }

  destroy(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.channel) {
      this.broadcast("tabClosed", { tabId: TAB_ID });
      this.channel.close();
      this.channel = null;
    }
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    window.removeEventListener("focus", this.handleFocus);
    window.removeEventListener("blur", this.handleBlur);
    this.stateListeners = [];
    this.callAnsweredListeners = [];
    this.commandListeners = [];
    this.remoteTabs.clear();
  }

  /**
   * Update local call state and broadcast to other tabs.
   */
  updateState(partial: Partial<Omit<TabCallState, "tabId" | "lastUpdated">>): void {
    this.localState = {
      ...this.localState,
      ...partial,
      lastUpdated: Date.now(),
      hasFocus: document.hasFocus(),
    };
    this.broadcast("callStateChanged", this.localState);
  }

  /**
   * Broadcast that a call has been answered on this tab.
   * All other tabs should immediately dismiss their ringing UI and reject
   * their local SIP session for this call.
   */
  broadcastCallAnswered(): void {
    this.broadcast("callAnswered", { answeredBy: TAB_ID });
  }

  /**
   * Broadcast that a call has been rejected on this tab.
   * All other tabs should also dismiss their ringing UI.
   */
  broadcastCallRejected(): void {
    this.broadcast("callRejected", { rejectedBy: TAB_ID });
  }

  /**
   * Determine if this tab is the "leader" for ringing.
   * The leader is the tab that should show the ringtone + full incoming call UI.
   *
   * Rules:
   * 1. If this tab has focus → it's the leader
   * 2. If NO tab has focus → the tab with the earliest TAB_ID is the leader
   *    (deterministic tiebreaker)
   */
  isRingtoneLeader(): boolean {
    // If this tab has focus, it's the leader
    if (document.hasFocus()) return true;

    // Check if any remote tab has focus
    const remoteEntries = Array.from(this.remoteTabs.values());
    for (let i = 0; i < remoteEntries.length; i++) {
      if (remoteEntries[i].hasFocus) {
        // Another tab has focus — it's the leader, not us
        return false;
      }
    }

    // No tab has focus — use earliest tab ID as tiebreaker
    const allTabIds = [TAB_ID];
    for (let i = 0; i < remoteEntries.length; i++) {
      allTabIds.push(remoteEntries[i].tabId);
    }
    allTabIds.sort();
    return allTabIds[0] === TAB_ID;
  }

  /**
   * Check if any other tab has already answered/connected a call.
   * If so, this tab should NOT ring (the call is already handled).
   * Only considers truly connected states, not ringing or calling.
   */
  isCallHandledByOtherTab(): boolean {
    const remoteEntries = Array.from(this.remoteTabs.values());
    for (let i = 0; i < remoteEntries.length; i++) {
      const state = remoteEntries[i];
      if (
        state.callProgress === "connected" ||
        state.callProgress === "connecting"
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get active call info from a remote tab (if any).
   * Returns the first remote tab that has an active (connected/on-hold) call.
   */
  getRemoteActiveCall(): TabCallState["activeCall"] | null {
    const remoteEntries = Array.from(this.remoteTabs.values());
    for (let i = 0; i < remoteEntries.length; i++) {
      const state = remoteEntries[i];
      if (state.activeCall && state.hasActiveCall) {
        return state.activeCall;
      }
    }
    return null;
  }

  /**
   * Subscribe to remote tab state changes.
   */
  onStateChange(listener: StateListener): () => void {
    this.stateListeners.push(listener);
    return () => {
      this.stateListeners = this.stateListeners.filter((l) => l !== listener);
    };
  }

  /**
   * Subscribe to "call answered on another tab" events.
   * When this fires, the current tab should dismiss ringing and reject its session.
   */
  onCallAnsweredRemotely(listener: CallAnsweredListener): () => void {
    this.callAnsweredListeners.push(listener);
    return () => {
      this.callAnsweredListeners = this.callAnsweredListeners.filter((l) => l !== listener);
    };
  }

  /**
   * Send a call control command to the tab that owns the active SIP session.
   * Used when a non-owning tab has "taken control" and wants to mute/hold/hangup.
   */
  sendCommand(cmd: CallCommandPayload): void {
    this.broadcast("callCommand", cmd);
  }

  /**
   * Subscribe to incoming call commands from a controlling tab.
   * Only the tab that owns the SIP session should listen for these.
   */
  onCommand(listener: CommandListener): () => void {
    this.commandListeners.push(listener);
    return () => {
      this.commandListeners = this.commandListeners.filter((l) => l !== listener);
    };
  }

  getTabId(): string {
    return TAB_ID;
  }

  // --- Private ---

  private handleMessage(msg: TabSyncMessage): void {
    if (msg.tabId === TAB_ID) return;

    switch (msg.type) {
      case "callStateChanged": {
        const state = msg.payload as TabCallState;
        this.remoteTabs.set(msg.tabId, state);
        // Notify state listeners
        for (let i = 0; i < this.stateListeners.length; i++) {
          try {
            this.stateListeners[i](state);
          } catch { /* ignore */ }
        }
        break;
      }
      case "callAnswered":
      case "callRejected": {
        // Another tab answered or rejected the call — dismiss local ringing
        for (let i = 0; i < this.callAnsweredListeners.length; i++) {
          try {
            this.callAnsweredListeners[i]();
          } catch { /* ignore */ }
        }
        break;
      }
      case "callCommand": {
        // A controlling tab is sending a call command to the session-owning tab
        const cmd = msg.payload as CallCommandPayload;
        for (let i = 0; i < this.commandListeners.length; i++) {
          try {
            this.commandListeners[i](cmd);
          } catch { /* ignore */ }
        }
        break;
      }
      case "tabClosed": {
        this.remoteTabs.delete(msg.tabId);
        break;
      }
      default:
        break;
    }
  }

  private broadcast(type: TabSyncEventType, payload: any): void {
    if (!this.channel) return;
    try {
      this.channel.postMessage({ type, tabId: TAB_ID, payload });
    } catch { /* channel might be closed */ }
  }

  private pruneStale(): void {
    const now = Date.now();
    const entries = Array.from(this.remoteTabs.entries());
    for (let i = 0; i < entries.length; i++) {
      const [tabId, state] = entries[i];
      if (now - state.lastUpdated > STALE_THRESHOLD_MS) {
        this.remoteTabs.delete(tabId);
      }
    }
  }

  private handleFocus = (): void => {
    this.localState.hasFocus = true;
    this.broadcast("callStateChanged", this.localState);
  };

  private handleBlur = (): void => {
    this.localState.hasFocus = false;
    this.broadcast("callStateChanged", this.localState);
  };

  private handleBeforeUnload = (): void => {
    if (this.channel) {
      this.broadcast("tabClosed", { tabId: TAB_ID });
    }
  };
}

/** Singleton instance */
export const tabSyncService = new TabSyncService();
